import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useProfile } from '@/hooks/useProfile';

// Types for forum entities
export interface ForumCategory {
  id: string;
  title: string;
  description?: string;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface ForumThread {
  id: string;
  title: string;
  category_id: string;
  user_id: string | null;
  is_pinned: boolean;
  is_locked: boolean;
  views_count: number;
  created_at: string;
  updated_at: string;
  user?: ForumUser;
  posts?: ForumPost[];
  replies?: number;
  lastActivity?: string;
}

export interface ForumPost {
  id: string;
  thread_id: string;
  user_id: string | null;
  content: string;
  created_at: string;
  updated_at: string;
  user?: ForumUser;
  likes?: number;
  liked?: boolean;
}

export interface ForumUser {
  id: string;
  name: string;
  avatar: string | null;
  role: string;
}

export function useForum() {
  const { user } = useAuth();
  const { profile } = useProfile();
  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [threads, setThreads] = useState<ForumThread[]>([]);
  const [posts, setPosts] = useState<ForumPost[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch all categories
  const fetchCategories = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("forum_categories")
      .select("*")
      .order("display_order", { ascending: true });
    if (error) {
      toast({ title: "Error", description: "Failed to load categories", variant: "destructive" });
      setCategories([]);
    } else {
      setCategories(data || []);
    }
    setLoading(false);
  }, [toast]);

  // Fetch all threads (optionally by category) and count replies for each thread
  const fetchThreads = useCallback(async (categoryId?: string) => {
    setLoading(true);
    let query = supabase
      .from("forum_threads")
      .select("*")
      .order("is_pinned", { ascending: false })
      .order("updated_at", { ascending: false });
    if (categoryId) {
      query = query.eq("category_id", categoryId);
    }
    const { data, error } = await query;
    if (error) {
      toast({ title: "Error", description: "Failed to load threads", variant: "destructive" });
      setThreads([]);
    } else {
      // Collect all unique user_ids
      const userIds = Array.from(new Set((data || []).map((t: any) => (t.user_id ? String(t.user_id).trim() : null)).filter(Boolean)));
      // Debug log userIds
      // eslint-disable-next-line no-console
      console.log("[Forum] Querying profiles for userIds:", userIds);
      let userMap: Record<string, ForumUser> = {};
      if (userIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from("profiles")
          .select("id, full_name, avatar_url, username, role")
          .in("id", userIds);
        if (!profilesError && profiles) {
          // Debug log
          // eslint-disable-next-line no-console
          console.log("[Forum] Loaded profiles for mapping:", profiles);
          if (profiles.length === 0) {
            // eslint-disable-next-line no-console
            console.warn("[Forum] WARNING: No profiles returned for userIds. Check RLS or data integrity.");
          }
          userMap = profiles.reduce((acc: Record<string, ForumUser>, p: any) => {
            const key = String(p.id).trim();
            acc[key] = {
              id: key,
              name: (p.full_name && p.full_name.trim()) || (p.username && p.username.trim()) || "User",
              avatar: p.avatar_url || null,
              role: (p.role && p.role.trim()) || "Member",
            };
            return acc;
          }, {});
        }
      }
      // Get all thread IDs
      const threadIds = (data || []).map((t: any) => t.id);
      let repliesMap: Record<string, number> = {};
      if (threadIds.length > 0) {
        // Fetch counts of posts per thread in a single query
        const { data: postCounts, error: postCountsError } = await supabase
          .from("forum_posts")
          .select("thread_id, id", { count: "exact" })
          .in("thread_id", threadIds);
        if (!postCountsError && postCounts) {
          repliesMap = postCounts.reduce((acc: Record<string, number>, post: any) => {
            acc[post.thread_id] = (acc[post.thread_id] || 0) + 1;
            return acc;
          }, {});
        }
      }
      // Map user info and replies count into threads
      const threadsWithUserAndReplies = (data || []).map((t: any) => {
        let userObj: ForumUser | undefined = undefined;
        const userIdKey = t.user_id ? String(t.user_id).trim() : null;
        if (userIdKey && userMap[userIdKey]) {
          userObj = userMap[userIdKey];
        } else if (userIdKey) {
          userObj = {
            id: userIdKey,
            name: "User",
            avatar: null,
            role: "Member",
          };
        }
        return {
          ...t,
          user: userObj,
          replies: repliesMap[t.id] || 0,
        };
      });
      setThreads(threadsWithUserAndReplies);
    }
    setLoading(false);
  }, [toast]);

  // Fetch posts for a thread
  const fetchPosts = useCallback(async (threadId: string) => {
    setLoading(true);
    const { data, error } = await supabase
      .from("forum_posts")
      .select("*")
      .eq("thread_id", threadId)
      .order("created_at", { ascending: true });
    if (error) {
      toast({ title: "Error", description: "Failed to load posts", variant: "destructive" });
      setPosts([]);
    } else {
      // Collect all unique user_ids
      const userIds = Array.from(new Set((data || []).map((p: any) => (p.user_id ? String(p.user_id).trim() : null)).filter(Boolean)));
      // Debug log userIds
      // eslint-disable-next-line no-console
      console.log("[Forum] Querying profiles for userIds (posts):", userIds);
      let userMap: Record<string, ForumUser> = {};
      if (userIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from("profiles")
          .select("id, full_name, avatar_url, username, role")
          .in("id", userIds);
        if (!profilesError && profiles) {
          // Debug log
          // eslint-disable-next-line no-console
          console.log("[Forum] Loaded profiles for post mapping:", profiles);
          if (profiles.length === 0) {
            // eslint-disable-next-line no-console
            console.warn("[Forum] WARNING: No profiles returned for userIds (posts). Check RLS or data integrity.");
          }
          userMap = profiles.reduce((acc: Record<string, ForumUser>, p: any) => {
            const key = String(p.id).trim();
            acc[key] = {
              id: key,
              name: (p.full_name && p.full_name.trim()) || (p.username && p.username.trim()) || "User",
              avatar: p.avatar_url || null,
              role: (p.role && p.role.trim()) || "Member",
            };
            return acc;
          }, {});
        }
      }
      // Map user info into posts
      const postsWithUser = (data || []).map((p: any) => {
        let userObj: ForumUser | undefined = undefined;
        const userIdKey = p.user_id ? String(p.user_id).trim() : null;
        if (userIdKey && userMap[userIdKey]) {
          userObj = userMap[userIdKey];
        } else if (userIdKey) {
          userObj = {
            id: userIdKey,
            name: "User",
            avatar: null,
            role: "Member",
          };
        }
        return {
          ...p,
          user: userObj,
        };
      });
      setPosts(postsWithUser);
    }
    setLoading(false);
  }, [toast]);

  // Add a new thread
  const addThread = useCallback(async (title: string, categoryId: string, content: string) => {
    if (!user) return null;
    const { data, error } = await supabase
      .from("forum_threads")
      .insert({
        title,
        category_id: categoryId,
        user_id: user.id,
      })
      .select()
      .single();
    if (error) {
      toast({ title: "Error", description: "Failed to create thread", variant: "destructive" });
      return null;
    }
    // Add first post
    if (data) {
      await supabase.from("forum_posts").insert({
        thread_id: data.id,
        user_id: user.id,
        content,
      });
      fetchThreads(categoryId);
      return data.id;
    }
    return null;
  }, [user, fetchThreads, toast]);

  // Add a reply to a thread
  const addReply = useCallback(async (threadId: string, content: string) => {
    if (!user) return null;
    const { data, error } = await supabase
      .from("forum_posts")
      .insert({
        thread_id: threadId,
        user_id: user.id,
        content,
      })
      .select()
      .single();
    if (error) {
      toast({ title: "Error", description: "Failed to post reply", variant: "destructive" });
      return null;
    }
    fetchPosts(threadId);
    fetchThreads();
    const displayName = profile?.full_name || profile?.username || "User";
    try {
      await supabase.functions.invoke("notify-thread-followers", {
        body: {
          thread_id: threadId,
          post_id: data.id,
          post_content: content,
          post_author_id: user.id,
          post_author_name: displayName,
          thread_title: threads.find(t => t.id === threadId)?.title || "Untitled Thread",
          post_url: window.location.href,
        },
      });
    } catch (invokeError) {
      console.log("Notification function failed:", invokeError);
    }
    return data.id;
  }, [user, profile, fetchPosts, fetchThreads, threads, toast]);

  // Edit a thread (title only)
  const editThread = useCallback(async (threadId: string, title: string) => {
    const { error } = await supabase
      .from("forum_threads")
      .update({ title })
      .eq("id", threadId);
    if (error) {
      toast({ title: "Error", description: "Failed to update thread", variant: "destructive" });
      return false;
    }
    toast({ title: "Success", description: "Thread updated." });
    // Refetch to show changes
    fetchThreads(threads.find(t => t.id === threadId)?.category_id);
    return true;
  }, [fetchThreads, threads, toast]);

  // Edit a post (content only)
  const editPost = useCallback(async (postId: string, content: string, threadId: string) => {
    const { error } = await supabase
      .from("forum_posts")
      .update({ content, updated_at: new Date().toISOString() })
      .eq("id", postId);
    if (error) {
      toast({ title: "Error", description: "Failed to update post", variant: "destructive" });
      return false;
    }
    toast({ title: "Success", description: "Post updated." });
    // Refetch posts for the current thread
    fetchPosts(threadId);
    return true;
  }, [fetchPosts, toast]);

  // Delete a thread
  const deleteThread = useCallback(async (threadId: string) => {
    const { error } = await supabase
      .from("forum_threads")
      .delete()
      .eq("id", threadId);
    if (error) {
      toast({ title: "Error", description: "Failed to delete thread", variant: "destructive" });
      return false;
    }
    toast({ title: "Success", description: "Thread deleted." });
    fetchThreads();
    return true;
  }, [fetchThreads, toast]);

  // Delete a post
  const deletePost = useCallback(async (postId: string, threadId: string) => {
    // First, check if this is the only post in the thread
    const { count, error: countError } = await supabase
      .from("forum_posts")
      .select('*', { count: 'exact', head: true })
      .eq("thread_id", threadId);

    if (countError) {
      toast({ title: "Error", description: "Could not check thread contents.", variant: "destructive" });
      return false;
    }

    // If this is the only post, delete the entire thread instead
    if (count === 1) {
      return await deleteThread(threadId);
    }

    // Otherwise, just delete the post
    const { error } = await supabase
      .from("forum_posts")
      .delete()
      .eq("id", postId);
    
    if (error) {
      toast({ title: "Error", description: "Failed to delete post", variant: "destructive" });
      return false;
    }
    
    toast({ title: "Success", description: "Post deleted." });
    fetchPosts(threadId);
    return true;
  }, [fetchPosts, toast, deleteThread]);

  // Delete a category
  const deleteCategory = useCallback(async (categoryId: string) => {
    // 1. Check if category has threads
    const { count, error: countError } = await supabase
      .from("forum_threads")
      .select('*', { count: 'exact', head: true })
      .eq("category_id", categoryId);

    if (countError) {
      toast({ title: "Error", description: "Could not check category contents.", variant: "destructive" });
      return false;
    }

    if (count && count > 0) {
      toast({ title: "Cannot delete", description: "Category is not empty. Please move or delete threads first.", variant: "destructive" });
      return false;
    }

    // 2. Delete category if empty
    const { error } = await supabase
      .from("forum_categories")
      .delete()
      .eq("id", categoryId);
    
    if (error) {
      toast({ title: "Error", description: "Failed to delete category.", variant: "destructive" });
      return false;
    }

    toast({ title: "Success", description: "Category deleted." });
    fetchCategories();
    return true;
  }, [fetchCategories, toast]);

  // Like/unlike a post
  const toggleLike = useCallback(async (postId: string, liked: boolean) => {
    if (!user) return;
    if (liked) {
      // Unlike
      const { error } = await supabase
        .from("forum_post_likes")
        .delete()
        .eq("post_id", postId)
        .eq("user_id", user.id);
      if (error) {
        toast({ title: "Error", description: "Failed to unlike post", variant: "destructive" });
      }
    } else {
      // Like
      const { error } = await supabase
        .from("forum_post_likes")
        .insert({
          post_id: postId,
          user_id: user.id,
        });
      if (error) {
        toast({ title: "Error", description: "Failed to like post", variant: "destructive" });
      }
    }
    // Optionally, refetch posts to update like count
  }, [user, toast]);

  // Fetch likes for a post
  const fetchLikes = useCallback(async (postId: string) => {
    const { data, error } = await supabase
      .from("forum_post_likes")
      .select("*")
      .eq("post_id", postId);
    if (error) return 0;
    return data.length;
  }, []);

  // Fetch if user liked a post
  const fetchUserLiked = useCallback(async (postId: string) => {
    if (!user) return false;
    const { data, error } = await supabase
      .from("forum_post_likes")
      .select("*")
      .eq("post_id", postId)
      .eq("user_id", user.id)
      .maybeSingle();
    if (error) return false;
    return !!data;
  }, [user]);

  // --- NEW: Increment thread views ---
  const incrementThreadViews = useCallback(async (threadId: string) => {
    try {
      const { error } = await supabase
        .rpc('increment_thread_views', { thread_id: threadId });
      if (error) {
        console.error('RPC increment_views failed', error);
        // Fallback: manually increment if RPC fails
        const { data: thread, error: fetchError } = await supabase
          .from("forum_threads")
          .select("views_count")
          .eq("id", threadId)
          .single();
        if (!fetchError && thread) {
          const newCount = (thread.views_count || 0) + 1;
          await supabase
            .from("forum_threads")
            .update({ views_count: newCount })
            .eq("id", threadId);
        }
      }
    } catch (err) {
      console.error('Increment thread views failed:', err);
    }
  }, []);

  // --- REALTIME SUBSCRIPTION FOR THREAD UPDATES ---
  useEffect(() => {
    // Subscribe to UPDATE events on forum_threads
    const channel = supabase
      .channel('realtime-forum-threads')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'forum_threads' },
        (payload) => {
          const updatedThread = payload.new;
          setThreads(prevThreads =>
            prevThreads.map(thread =>
              thread.id === updatedThread.id
                ? { ...thread, ...updatedThread }
                : thread
            )
          );
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'forum_posts' },
        (payload) => {
          // When a new post is added, update the replies count for the thread
          const threadId = payload.new.thread_id;
          setThreads(prevThreads =>
            prevThreads.map(thread =>
              thread.id === threadId
                ? { ...thread, replies: (thread.replies || 0) + 1 }
                : thread
            )
          );
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Initial load
  useEffect(() => {
    fetchCategories();
    fetchThreads();
  }, [fetchCategories, fetchThreads]);

  return {
    categories,
    threads,
    posts,
    loading,
    fetchCategories,
    fetchThreads,
    fetchPosts,
    addThread,
    addReply,
    toggleLike,
    fetchLikes,
    fetchUserLiked,
    incrementThreadViews,
    editThread,
    editPost,
    deleteThread,
    deletePost,
  };
}